import styles from './submission-detail-panel.module.css'

import { LuAudioLines } from '@react-icons/all-files/lu/LuAudioLines'
import { LuChevronLeft } from '@react-icons/all-files/lu/LuChevronLeft'
import { LuChevronRight } from '@react-icons/all-files/lu/LuChevronRight'
import React from 'react'

import { type TFunction, useTranslation } from '@disco/i18n/use-translation'
import { SlideoutPanel } from '@disco/shared-components/ui/slideout-panel'
import {
  type LicenseRequestSubmission,
  type LicenseRequestSubmissionTrack,
  useGetRequestFormVersionQuery,
} from '@disco/state-management/api/license-requests'
import { getInitials } from '@disco/utils/string-utils'

import type { FormFieldSchema } from 'types/license-requests'
import type { Track, TrackId } from 'types/track'

import {
  Avatar,
  Box,
  Flex,
  IconButton,
  Separator,
  Spinner,
  Text,
} from 'components/ds'
import TrackTooltip from 'components/track-tooltip'

import { formatDate } from '../format-date'
import { TrackMetadataModal } from './track-metadata-modal'

/** Fields rendered as static entries or handled by the track list */
const SKIP_FIELDS = new Set(['user_name', 'user_email', 'tracks', 'playlist'])

/**
 * Answers store the schema's raw `const`, so `{ const: 1, title: 'Small' }`
 * arrives as `1`. Show the title the requester saw instead.
 */
const toOptionLabel = (
  value: unknown,
  field: FormFieldSchema | undefined,
): unknown => {
  const options = field?.oneOf ?? field?.items?.oneOf

  if (!options) {
    return value
  }

  return options.find(option => option.const === value)?.title ?? value
}

/**
 * React renders an array of strings with no separator, and drops booleans
 * entirely. Format both so they show correctly.
 */
const formatAnswer = (
  t: TFunction,
  value: unknown,
  field?: FormFieldSchema,
): React.ReactNode => {
  if (Array.isArray(value)) {
    return value.map(item => toOptionLabel(item, field)).join(', ')
  }

  if (typeof value === 'boolean') {
    return value ? t('common:yes', 'Yes') : t('common:no', 'No')
  }

  return toOptionLabel(value, field) as React.ReactNode
}

type FieldRowProps = {
  label: string
  value: React.ReactNode
}

const FieldRow = ({ label, value }: FieldRowProps) => (
  <Flex direction="column" gap="1">
    <Text size="2" weight="medium">
      {label}
    </Text>
    <Box className={styles.fieldValue}>
      <Text size="2">{value == null || value === '' ? '' : value}</Text>
    </Box>
  </Flex>
)

type TrackInfoPopoverProps = {
  track: LicenseRequestSubmissionTrack
  onViewFullInfo: () => void
}

const TrackInfoPopover = ({ track, onViewFullInfo }: TrackInfoPopoverProps) => {
  return (
    <TrackTooltip
      track={{ ...track, uploaded: true, metadata: {} } as unknown as Track}
      showViewLink
      onViewFullInfo={onViewFullInfo}
      iconClassName={styles.trackInfoIcon}
      trigger="click"
    />
  )
}

type Props = {
  open: boolean
  submission: LicenseRequestSubmission | null
  onClose: () => void
  onPrev: () => void
  onNext: () => void
  prevDisabled: boolean
  nextDisabled: boolean
}

export const SubmissionDetailPanel = ({
  open,
  submission,
  onClose,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
}: Props) => {
  const { t } = useTranslation('common')
  // Keep rendering the last submission while `open` transitions to false so
  // the panel has content to show during its slide-out animation, since the
  // caller clears `submission` to null in the same render as closing.
  const [lastSubmission, setLastSubmission] =
    React.useState<LicenseRequestSubmission | null>(submission)

  React.useEffect(() => {
    if (submission) {
      setLastSubmission(submission)
    }
  }, [submission])

  const displaySubmission = submission ?? lastSubmission

  const { data: formVersion, isLoading: formVersionLoading } =
    useGetRequestFormVersionQuery(displaySubmission?.form_version_id ?? 0, {
      skip: !displaySubmission,
    })
  const [viewingTrackId, setViewingTrackId] = React.useState<TrackId | null>(
    null,
  )

  React.useEffect(() => {
    setViewingTrackId(null)
  }, [submission?.id])

  if (!displaySubmission) return null

  const { answers, tracks } = displaySubmission
  const initials = getInitials(String(answers.user_name ?? ''))

  const playlistName = answers.playlist?.name

  const dynamicFields = (() => {
    if (!formVersion) return []
    const { order } = formVersion.definition.ui
    const { properties } = formVersion.definition.schema
    // Mirrors resolveFields in the catalog form, which appends properties the
    // definition forgot to order. Those are answerable, so they must show here.
    const orderedKeys = [
      ...order.filter(key => key in properties),
      ...Object.keys(properties).filter(key => !order.includes(key)),
    ]
    return orderedKeys
      .filter(key => !SKIP_FIELDS.has(key))
      .filter(key => {
        const field = properties[key]
        if (!field) return false
        const discoType = field['x-disco-type']
        return (
          discoType !== 'track_reference' && discoType !== 'playlist_reference'
        )
      })
      .map(key => ({
        key,
        label: properties[key]?.title ?? key,
        value: formatAnswer(t, answers[key], properties[key]),
      }))
  })()

  return (
    <SlideoutPanel
      open={open}
      onClose={onClose}
      header={
        <Flex align="center" gap="2" p="5" pb="4" mt="8">
          <Avatar
            size="1"
            fallback={initials || '?'}
            radius="full"
            className={styles.avatar}
          />
          <Text size="3" weight="bold" className={styles.headerName}>
            {answers.user_name}
          </Text>
        </Flex>
      }
      zIndex={4}
      closeOnClickOutside={false}
      closeButtonClassName={styles.closeButton}
      headerActions={
        <Flex gap="2" mt="8">
          <IconButton
            size="1"
            variant="soft"
            color="gray"
            disabled={prevDisabled}
            onClick={onPrev}
            aria-label="Previous"
          >
            <LuChevronLeft size={22} />
          </IconButton>
          <IconButton
            size="1"
            variant="soft"
            color="gray"
            disabled={nextDisabled}
            onClick={onNext}
            aria-label="Next"
          >
            <LuChevronRight size={22} />
          </IconButton>
        </Flex>
      }
    >
      <Flex direction="column" gap="3" px="5">
        <FieldRow
          label="Submission Date"
          value={formatDate(displaySubmission.created)}
        />
        <FieldRow label="Request ID" value={displaySubmission.id} />
        <FieldRow label="Name" value={String(answers.user_name ?? '')} />
        <FieldRow label="Email" value={String(answers.user_email ?? '')} />

        {formVersionLoading && (
          <Flex justify="center" py="4">
            <Spinner />
          </Flex>
        )}

        {dynamicFields.map(field => (
          <FieldRow
            key={field.key}
            label={field.label}
            value={field.value != null ? String(field.value) : undefined}
          />
        ))}

        {tracks.length > 0 && (
          <>
            <Separator size="4" />
            <Text size="2" weight="medium">
              <span>{playlistName && `${playlistName} | `}</span>
              {`Tracks (${tracks.length})`}
            </Text>
            <Flex direction="column" gap="3">
              {tracks.map(track => (
                <Flex key={track.id} align="center" gap="3">
                  {track.artwork?.artwork_sizes['160x160'] ? (
                    <img
                      src={track.artwork.artwork_sizes['160x160']}
                      alt={`Artwork for ${track.name}`}
                      className={styles.trackArtwork}
                    />
                  ) : (
                    <Flex
                      align="center"
                      justify="center"
                      className={styles.trackArtworkPlaceholder}
                    >
                      <LuAudioLines size={20} />
                    </Flex>
                  )}
                  <Flex direction="column" flexGrow="1" minWidth="0">
                    <Text size="2" weight="medium" truncate>
                      {track.name}
                    </Text>
                    <Text size="1" color="gray" truncate>
                      {track.artist}
                    </Text>
                  </Flex>
                  <TrackInfoPopover
                    track={track}
                    onViewFullInfo={() => setViewingTrackId(track.id)}
                  />
                </Flex>
              ))}
            </Flex>
          </>
        )}
      </Flex>
      <TrackMetadataModal
        trackId={viewingTrackId}
        onClose={() => setViewingTrackId(null)}
      />
    </SlideoutPanel>
  )
}
